/**
 * Question Helper Utilities
 *
 * Utility functions for processing and grouping questions by context,
 * and validating user answers against expected answers.
 * Used in AdminPanel, PracticeEngine, and other question management components.
 *
 * @module questionHelpers
 */

import { compareTwoStrings } from 'string-similarity';

export interface QuestionWithIndex {
  [key: string]: any;
  originalIndex: number;
}

export interface ContextGroups {
  [contextName: string]: QuestionWithIndex[];
}

/**
 * Extracts the context key from a question object.
 * Handles different question structures (sample vs generated questions).
 *
 * @param question - Question object with potential context information
 * @param fallbackName - Default name to use if no context is found
 * @returns The context key/name for grouping
 */
export function getContextKey(
  question: any,
  fallbackName: string = 'No Context',
): string {
  // For generated questions, check context_name first
  if (question.context_name) {
    return question.context_name;
  }

  // For sample questions or other structures, check context.name
  if (question.context?.name) {
    return question.context.name;
  }

  return fallbackName;
}

/**
 * Groups questions by their context name/key.
 * Preserves the original index of each question.
 *
 * @param questions - Array of questions to group
 * @param fallbackName - Default context name for questions without context (default: 'No Context')
 * @returns Object mapping context names to arrays of questions with indices
 *
 * @example
 * const grouped = groupQuestionsByContext(questions, 'Uncategorized');
 * // Returns: { 'Context 1': [q1, q2], 'Context 2': [q3, q4], ... }
 */
export function groupQuestionsByContext(
  questions: any[],
  fallbackName: string = 'No Context',
): ContextGroups {
  const contextGroups: ContextGroups = {};

  questions.forEach((question, index) => {
    const contextKey = getContextKey(question, fallbackName);

    if (!contextGroups[contextKey]) {
      contextGroups[contextKey] = [];
    }

    contextGroups[contextKey].push({ ...question, originalIndex: index });
  });

  return contextGroups;
}

/**
 * Question type definition for answer validation
 */
export interface QuestionForValidation {
  type: string;
  expected_answer: string | string[];
  options?: string[];
}

/**
 * Validates array-based answers
 */
function validateArrayAnswer(
  question: QuestionForValidation,
  userAnswer: string[],
): boolean {
  const { expected_answer } = question;
  if (Array.isArray(expected_answer)) {
    return (
      expected_answer.every((ans) => userAnswer.includes(ans)) &&
      userAnswer.every((ans) => expected_answer.includes(ans))
    );
  }
  if (userAnswer.length === 1) {
    return validateAnswer(question, userAnswer[0]);
  }
  return false;
}

/**
 * Validates multiple choice single answer
 */
function validateMultipleChoiceSingle(
  userAnswerStr: string,
  expectedAnswer: string | string[],
): boolean {
  return userAnswerStr === String(expectedAnswer);
}

/**
 * Validates true/false answer
 */
function validateTrueFalse(
  userAnswerStr: string,
  expectedAnswer: string | string[],
): boolean {
  return userAnswerStr.toLowerCase() === String(expectedAnswer).toLowerCase();
}

/**
 * Validates text answer (case-insensitive, trimmed)
 */
function validateTextAnswer(
  userAnswerStr: string,
  expectedAnswer: string | string[],
): boolean {
  const userTrimmed = userAnswerStr.trim().toLowerCase();
  const correctTrimmed = String(expectedAnswer).trim().toLowerCase();
  return userTrimmed === correctTrimmed;
}

/**
 * Validates multiple selection answer (order-independent)
 */
function validateMultipleSelection(
  userAnswer: string | string[],
  userAnswerStr: string,
  expectedAnswer: string | string[],
): boolean {
  if (!userAnswerStr || !expectedAnswer) return false;

  try {
    let userSelections: string[];
    let correctSelections: string[];

    if (Array.isArray(expectedAnswer)) {
      correctSelections = expectedAnswer
        .map(String)
        .sort((a, b) => a.localeCompare(b));
    } else {
      correctSelections = JSON.parse(String(expectedAnswer))
        .map(String)
        .sort((a, b) => a.localeCompare(b));
    }

    if (typeof userAnswer === 'string' && userAnswer.startsWith('[')) {
      userSelections = JSON.parse(userAnswer)
        .map(String)
        .sort((a, b) => a.localeCompare(b));
    } else if (Array.isArray(userAnswer)) {
      userSelections = userAnswer
        .map(String)
        .sort((a, b) => a.localeCompare(b));
    } else {
      userSelections = [userAnswerStr].sort((a, b) => a.localeCompare(b));
    }

    return (
      JSON.stringify(userSelections) === JSON.stringify(correctSelections)
    );
  } catch {
    return false;
  }
}

/**
 * Validates if a user's answer matches the expected answer for a given question.
 * Handles multiple question types including multiple choice, true/false, text answers,
 * and multiple selection questions.
 *
 * @param question - Question object with type and expected_answer
 * @param userAnswer - User's answer (string or string array)
 * @returns True if the answer is correct, false otherwise
 *
 * @example
 * // Multiple choice
 * validateAnswer({ type: 'multiple-choice-single', expected_answer: 'A' }, 'A') // true
 *
 * // Text answer
 * validateAnswer({ type: 'text-short', expected_answer: 'Hello' }, 'hello') // true (case-insensitive)
 *
 * // Multiple selection
 * validateAnswer({
 *   type: 'multiple-choice-multi',
 *   expected_answer: JSON.stringify(['A', 'B'])
 * }, JSON.stringify(['B', 'A'])) // true (order-independent)
 */
export function validateAnswer(
  question: QuestionForValidation,
  userAnswer: string | string[],
): boolean {
  const { type, expected_answer } = question;

  // Handle array inputs for multiple selection questions
  if (Array.isArray(userAnswer)) {
    return validateArrayAnswer(question, userAnswer);
  }

  // Handle string inputs
  const userAnswerStr = String(userAnswer);

  // Handle multiple question type aliases
  switch (type) {
    case 'multiple-choice-single':
      return validateMultipleChoiceSingle(userAnswerStr, expected_answer);

    case 'true-false':
      return validateTrueFalse(userAnswerStr, expected_answer);

    case 'fill-in-blank':
    case 'text-short':
    case 'text-long':
      return validateTextAnswer(userAnswerStr, expected_answer);

    case 'multiple-choice-multi':
      return validateMultipleSelection(
        userAnswer,
        userAnswerStr,
        expected_answer,
      );

    default:
      // Default: simple string comparison
      if (Array.isArray(expected_answer)) {
        return expected_answer.includes(userAnswerStr);
      }
      return userAnswerStr === String(expected_answer);
  }
}

/**
 * Simple answer checker for array-based comparisons.
 * Used for practice questions where answers may be arrays.
 *
 * @param userAnswer - User's answer (string or string array)
 * @param expectedAnswer - Expected answer (string or string array)
 * @returns True if answers match
 */
export function checkAnswer(
  userAnswer: string | string[],
  expectedAnswer: string | string[],
): boolean {
  return validateAnswer(
    { type: 'auto', expected_answer: expectedAnswer },
    userAnswer,
  );
}

/**
 * Normalizes a string for fuzzy comparison by:
 * - Trimming whitespace
 * - Converting to lowercase
 * - Removing parenthetical optional parts like (39 Grad/º)
 * - Collapsing multiple spaces to single space
 *
 * @param text - Text to normalize
 * @returns Normalized text
 */
function normalizeForFuzzyMatch(text: string): string {
  return (
    text
      .trim()
      .toLowerCase()
      // Remove parenthetical parts like (39 Grad/º) or (text)
      .replaceAll(/\([^)]*\)/g, '')
      // Collapse multiple spaces to single space
      .replaceAll(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Validates answer using fuzzy string matching with 80% similarity threshold.
 * Useful for short-text answers where minor typos should be acceptable.
 *
 * @param question - Question object with expected_answer
 * @param userAnswer - User's answer
 * @param threshold - Similarity threshold (0.0 to 1.0), defaults to 0.80
 * @returns True if answer is correct (matches with >= threshold similarity)
 *
 * @example
 * // Exact match
 * validateAnswerWithFuzzyMatch({ expected_answer: 'Fieber' }, 'Fieber') // true
 *
 * // Minor typo
 * validateAnswerWithFuzzyMatch({ expected_answer: 'Fieber' }, 'Feiber') // true (85% similar)
 *
 * // Array of accepted answers
 * validateAnswerWithFuzzyMatch({
 *   expected_answer: ['Ich habe (39 Grad/º) Fieber', '(39 Grad/º) Fieber']
 * }, 'Ich habe 39 Grad Fieber') // true
 *
 * // Too different
 * validateAnswerWithFuzzyMatch({ expected_answer: 'Fieber' }, 'Kopfschmerzen') // false
 */
export function validateAnswerWithFuzzyMatch(
  question: QuestionForValidation,
  userAnswer: string | string[],
  threshold: number = 0.95, // higher threshold for more lenient matching
): boolean {
  const { expected_answer } = question;

  // Handle array user answer (shouldn't happen for text questions, but be safe)
  if (Array.isArray(userAnswer)) {
    if (userAnswer.length === 1) {
      return validateAnswerWithFuzzyMatch(question, userAnswer[0], threshold);
    }
    return false;
  }

  const userAnswerStr = String(userAnswer);
  const normalizedUserAnswer = normalizeForFuzzyMatch(userAnswerStr);

  const expectedAnswers: string[] = (() => {
    if (!expected_answer && expected_answer !== '') {
      return [];
    }

    if (Array.isArray(expected_answer)) {
      return expected_answer.map(String);
    }

    const expectedAnswerStr = String(expected_answer).trim();

    // Some writing questions store arrays as JSON strings (e.g. ["Option A", "Option B"])
    if (expectedAnswerStr.startsWith('[') && expectedAnswerStr.endsWith(']')) {
      try {
        const parsed = JSON.parse(expectedAnswerStr);
        if (Array.isArray(parsed)) {
          return parsed.map(String);
        }
      } catch {
        // Fall back to treating as single string if JSON parsing fails
      }
    }

    return [expectedAnswerStr];
  })();

  if (expectedAnswers.length === 0) {
    return false;
  }

  return expectedAnswers.some((expectedOption) => {
    const normalizedCorrect = normalizeForFuzzyMatch(expectedOption);
    const similarity = compareTwoStrings(
      normalizedUserAnswer,
      normalizedCorrect,
    );
    return similarity >= threshold;
  });
}
